import { allocationCellKey } from '@ovComponents/4-module/widgets/portfolio/components/allocationCellKey';
import { useCallback } from 'react';
import {
  type CellUploadInput,
  cellStateOf,
  resolveCellRelease,
  resolveCellUpload,
  toUploadedCellValues,
} from './cellStates';
import type { CellEditPatch } from './resolveCellEdit';
import { useRejectOutOfRange } from './useRejectOutOfRange';

export const useCellStateActions = ({
  lockedCells,
  lockedAmountCents,
  uploadedCells,
  mvCentsOf,
  rejectOverCapacity,
  applyPatch,
}: {
  lockedCells: Map<string, number>;
  lockedAmountCents: Map<string, number>;
  uploadedCells: Set<string>;
  mvCentsOf: (subAccountId: string) => number;
  rejectOverCapacity: (
    key: string,
    mv: number,
    fraction: number,
    amountCents?: number,
  ) => boolean;
  applyPatch: (patch: CellEditPatch) => void;
}) => {
  const rejectOutOfRange = useRejectOutOfRange();

  const cellState = useCallback(
    (sa: string, fp: string) =>
      cellStateOf(allocationCellKey(sa, fp), { lockedCells, uploadedCells }),
    [lockedCells, uploadedCells],
  );

  const releaseCell = useCallback(
    (sa: string, fp: string) => {
      const patch = resolveCellRelease({
        key: allocationCellKey(sa, fp),
        lockedCells,
        lockedAmountCents,
        uploadedCells,
      });
      if (patch) applyPatch(patch);
    },
    [lockedCells, lockedAmountCents, uploadedCells, applyPatch],
  );

  // True when the cells were applied. A rejection has already toasted why.
  const uploadCells = useCallback(
    (cells: CellUploadInput[]): boolean => {
      if (rejectOutOfRange(cells.map((cell) => cell.fraction))) return false;

      const uploads = toUploadedCellValues(cells, mvCentsOf);
      if (
        uploads.some(({ key, mv, fraction, amountCents }) =>
          rejectOverCapacity(key, mv, fraction, amountCents),
        )
      ) {
        return false;
      }

      const patch = resolveCellUpload(uploads, {
        lockedCells,
        lockedAmountCents,
        uploadedCells,
      });
      if (!patch) return false;
      applyPatch(patch);
      return true;
    },
    [
      lockedCells,
      lockedAmountCents,
      uploadedCells,
      mvCentsOf,
      rejectOutOfRange,
      rejectOverCapacity,
      applyPatch,
    ],
  );

  return { cellState, releaseCell, uploadCells };
};
